import {
  $lang,
  $translationCtx,
  ModuleNotFoundError,
  providerFromManifest,
  SectionByMicroservice,
  Translation,
  useApiClient,
  useCompilationManifest,
  useEntityBaseMetadata,
  useLocalStorage,
  useMicroservices,
  useModuleBaseMetadata,
} from '@msdk/common'
import { EntityMetadata, LoadedSection } from '@msdk/definitions'
import React, { createContext, useContext, useEffect } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'

import { SectionData } from './section-data'
import { $i18nUpdater, $i18Version, I18N_LOCAL_STORAGE_KEY } from './selectors'

export interface i18n {
  load: (moduleMetadata: EntityMetadata, entityMedata: EntityMetadata, moduleName: string) => Promise<void>
}

const INITIAL_STATE: i18n = {
  load: (_moduleMetadata: EntityMetadata, _entityMedata: EntityMetadata, _moduleName: string) => {
    return Promise.resolve()
  },
}

const i18nContext = createContext<i18n>(INITIAL_STATE)
export const useI18nContext = () => useContext(i18nContext)

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const { getValue } = useLocalStorage()
  const microservices = useMicroservices()
  const { pathname } = useLocation()
  const params =
    matchPath(':tenant/:sitemap/:entityName/:moduleName', pathname) ||
    matchPath(':tenant/:sitemap/:entityName/:moduleName/:id', pathname)
  const [, setTranslationCtx] = useRecoilState($translationCtx)
  const updateContext = useSetRecoilState($i18nUpdater)
  const version = useRecoilValue($i18Version)
  const lang = useRecoilValue($lang)
  const manifest = useCompilationManifest()
  const entityBaseMetadata = useEntityBaseMetadata(params?.params.entityName as string)
  const moduleBaseMetadata = useModuleBaseMetadata(
    params?.params.entityName as string,
    params?.params.moduleName as string,
  )
  const client = useApiClient()

  /**
   * get the interested sections of translation given the current url
   */
  const getSections = (moduleMetadata: EntityMetadata, entityMetadata: EntityMetadata, moduleName: string) => {
    const entityName = entityMetadata?.name
    const moduleMsProvider = moduleMetadata?.msProvider as string
    const entityMsProvider = entityMetadata?.msProvider as string

    if (entityName === 'CUSTOM' || !entityName) {
      try {
        const { provider } = providerFromManifest(manifest, moduleName)
        return [
          { msName: provider, sectionName: 'CUSTOM' },
          { msName: provider, sectionName: provider },
        ]
      } catch (error) {
        if (error instanceof ModuleNotFoundError) {
          return []
        }

        throw error
      }
    } else {
      const sections: SectionByMicroservice[] = []

      // every section must consider the relevant microservice where the translation is stored
      sections.push({ msName: entityMsProvider, sectionName: entityName })
      sections.push({
        msName: entityMsProvider,
        sectionName: entityMsProvider,
      })

      if (moduleMetadata && entityMsProvider !== moduleMsProvider) {
        sections.push({ msName: moduleMsProvider, sectionName: entityName })
        sections.push({
          msName: moduleMsProvider,
          sectionName: moduleMsProvider,
        })
      }
      return sections
    }
  }

  const loadTranslation = async (
    sectionsToLoad: SectionByMicroservice[],
    requestedLocale: string,
  ): Promise<SectionData[]> => {
    const res: SectionData[] = []
    for (let i = 0; i < sectionsToLoad.length; i++) {
      const currentSection = sectionsToLoad[i]
      const loadedSection = await loadTranslationForSection(currentSection, requestedLocale)
      if (loadedSection) {
        res.push({ loadedSection, sectionInfo: currentSection })
      }
    }
    return res
  }

  const loadTranslationForSection = async (
    section: SectionByMicroservice,
    requestedLocale: string,
  ): Promise<LoadedSection | undefined> => {
    try {
      return await client.i18n.loadTranslations(section, requestedLocale)
    } catch (e) {
      console.log(e)
      return undefined
    }
  }

  function isEquivalentSection(sectionA: SectionByMicroservice, sectionB: SectionByMicroservice): boolean {
    return sectionA.msName === sectionB.msName && sectionA.sectionName === sectionB.sectionName
  }

  const needToReloadTranslation = (translation: Translation): boolean => {
    return !translation || translation.version !== version || translation.currentLocale !== lang
  }

  const reloadTranslation = async (
    interestedSections: SectionByMicroservice[],
    requestedLocale: string,
    requestedVersion: string,
  ) => {
    // Capture the locale and version used to start this load. The updater receives them with the
    // result and can discard it if either value changes while the section requests are pending.
    const missingSectionForInternalMs = sectionForInternalActiveMs(interestedSections)
    const sections = await loadTranslation(missingSectionForInternalMs, requestedLocale)
    updateContext({
      locale: requestedLocale,
      version: requestedVersion,
      sections,
    })
  }

  const missSomeSections = (translation: Translation, interestedSections: SectionByMicroservice[]): boolean => {
    return !interestedSections.every((section) =>
      translation.loadedSections.some((loadedSection) => isEquivalentSection(loadedSection, section)),
    )
  }

  const loadMissingSections = async (
    translation: Translation,
    interestedSections: SectionByMicroservice[],
    requestedLocale: string,
    requestedVersion: string,
  ) => {
    const missingSections = translation
      ? interestedSections.filter(
          (interestedSection) =>
            !translation.loadedSections.some((loadedSection) => isEquivalentSection(loadedSection, interestedSection)),
        )
      : interestedSections

    const missingSectionForInternalMs = sectionForInternalActiveMs(missingSections)
    const sections = await loadTranslation(missingSectionForInternalMs, requestedLocale)
    updateContext({
      locale: requestedLocale,
      version: requestedVersion,
      sections,
    })
  }

  const load = async (moduleMetadata: EntityMetadata, entityMedata: EntityMetadata, moduleName: string) => {
    const local = getValue(I18N_LOCAL_STORAGE_KEY)
    const translation = local?.value as Translation
    const interestedSections = getSections(moduleMetadata, entityMedata, moduleName)

    if (needToReloadTranslation(translation)) {
      await reloadTranslation(interestedSections, lang, version)
    } else if (missSomeSections(translation, interestedSections)) {
      await loadMissingSections(translation, interestedSections, lang, version)
    } else {
      setTranslationCtx({ ...translation })
    }
  }

  const sectionForInternalActiveMs = (sections: SectionByMicroservice[]): SectionByMicroservice[] => {
    const internalMicroservices = microservices.filter((ms) => ms.usesFramework && ms.isOk)
    return sections.filter((section) => internalMicroservices.find((ms) => ms.name === section.msName))
  }

  useEffect(() => {
    if (params?.params.entityName && params?.params.moduleName) {
      load(moduleBaseMetadata as EntityMetadata, entityBaseMetadata as EntityMetadata, params.params.moduleName).catch(
        (err) => console.log(err),
      )
    }
    // Reload when the route, authenticated language, or available translation version changes.
    // A Profile language change can update `lang` while leaving `pathname` unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, lang, version])

  return <i18nContext.Provider value={{ load }}>{children}</i18nContext.Provider>
}
